import {
    useState,
    useEffect,
    ChangeEventHandler,
    KeyboardEventHandler,
} from 'react';
import { ICloseEvent } from '@core/interfaces/ICloseEvent';
import { useStore } from '@ui/hooks/useStore';
import useKeyDown from '@ui/hooks/useKeyDown';
import readAsArrayBuffer from '@utils/readAsArrayBuffer';
import { IState } from './IState';

const useAddDeviceInDb = (onClose: ICloseEvent['onClose']) => {
    const { devices, download, lang } = useStore();
    const key = useKeyDown();

    const initState: IState = {
        imei: '',
        imeiError: false,
        type: '',
        typeError: false,
        password: '',
        passwordError: false,
        swVer: '',
        swVerError: false,
        bootVer: '',
        bootVerError: false,
        hwVer: '',
        hwVerError: false,
    };
    const [state, setState] = useState(initState);

    const imeiValidate = (imei: string) => {
        const regExp = /^\d{15,16}$/;
        if (!imei || !regExp.test(imei)) {
            setState((prev) => ({
                ...prev,
                imeiError: true,
            }));
            return false;
        } else {
            setState((prev) => ({
                ...prev,
                imeiError: false,
            }));
            return true;
        }
    };
    const typeValidate = (type: string) => {
        if (!type) {
            setState((prev) => ({
                ...prev,
                typeError: true,
            }));
            return false;
        } else {
            setState((prev) => ({
                ...prev,
                typeError: false,
            }));
            return true;
        }
    };
    const handleInputClick = () => {
        download.resetUploadError();
        devices.resetServerError();
        devices.resetDeviceAlreadyExistError();
        setState((prev) => ({
            ...prev,
            imeiError: false,
            typeError: false,
            passwordError: false,
            swVerError: false,
            hwVerError: false,
        }));
    };
    const handleTypeChange: ChangeEventHandler<HTMLInputElement> = (e) => {
        handleInputClick();
        const type = e.target.value;
        setState((prev) => ({
            ...prev,
            type,
        }));
    };
    const handlePasswordChange: ChangeEventHandler<HTMLInputElement> = (e) => {
        handleInputClick();
        const password = e.target.value;
        setState((prev) => ({
            ...prev,
            password,
        }));
    };
    const swVerValidate = (swVer: string) => {
        const regExp = /^\d+\.\d+$/;
        if (!swVer || !regExp.test(swVer)) {
            setState((prev) => ({
                ...prev,
                swVerError: true,
            }));
            return false;
        } else {
            setState((prev) => ({
                ...prev,
                swVerError: false,
            }));
            return true;
        }
    };
    const bootVerValidate = (bootVer: string) => {
        const regExp = /^\d+\.\d+$/;
        if (!bootVer || !regExp.test(bootVer)) {
            setState((prev) => ({
                ...prev,
                bootVerError: true,
            }));
            return false;
        } else {
            setState((prev) => ({
                ...prev,
                bootVerError: false,
            }));
            return true;
        }
    };
    const hwVerValidate = (hwVer: string) => {
        if (!hwVer) {
            setState((prev) => ({
                ...prev,
                hwVerError: true,
            }));
            return false;
        } else {
            setState((prev) => ({
                ...prev,
                hwVerError: false,
            }));
            return true;
        }
    };
    const handleImeiChange: ChangeEventHandler<HTMLInputElement> = (e) => {
        handleInputClick();
        const filter = /\D/g;
        const reqExp = /.{0,16}/;
        const val = e.target.value.replace(filter, '').match(reqExp);
        const imei = val ? val[0] : '';
        setState((prev) => ({
            ...prev,
            imei,
        }));
    };
    const handleSwVerChange: ChangeEventHandler<HTMLInputElement> = (e) => {
        handleInputClick();
        const filter = /[^0-9\.]/g;
        const reqExp = /(\d{1,3}(\.\d{0,3})?)/;
        const val = e.target.value.replace(filter, '').match(reqExp);
        const swVer = val ? val[0] : '';
        setState((prev) => ({
            ...prev,
            swVer,
        }));
    };
    const handleHwVerChange: ChangeEventHandler<HTMLInputElement> = (e) => {
        handleInputClick();
        const hwVer = e.target.value;
        setState((prev) => ({
            ...prev,
            hwVer,
        }));
    };
    const handleBootVerChange: ChangeEventHandler<HTMLInputElement> = (e) => {
        handleInputClick();
        const filter = /[^0-9\.]/g;
        const reqExp = /(\d{1,3}(\.\d{0,3})?)/;
        const val = e.target.value.replace(filter, '').match(reqExp);
        const bootVer = val ? val[0] : '';
        setState((prev) => ({
            ...prev,
            bootVer,
        }));
    };
    const handleAddClick = () => {
        handleInputClick();
        const { imei, type, password, swVer, hwVer, bootVer } = state;
        const isImeiValid = imeiValidate(imei);
        const isTypeValid = typeValidate(type);
        const isSwVerValid = swVerValidate(swVer);
        const isHwVerValid = hwVerValidate(hwVer);
        const isBootVerValid = bootVerValidate(bootVer);
        const isValid =
            isImeiValid && isTypeValid && isSwVerValid && isHwVerValid && isBootVerValid;
        if (isValid) {
            const [application_version_major, application_version_minor] = swVer.split('.');
            const [bootloader_version_major, bootloader_version_minor] = bootVer.split('.');
            const args = {
                imei,
                type,
                password,
                hw_version: hwVer,
                bootloader_version_major,
                bootloader_version_minor,
                application_version_major,
                application_version_minor,
            };
            devices.addDeviceInDb(args);
        }
    };
    const handleTemplateDownload = () => {
        handleInputClick();
        download.downloadDbDevicesTemplate();
    };
    const handleDevicesUpload: ChangeEventHandler<HTMLInputElement> = async ({
        target,
    }) => {
        handleInputClick();
        const { files } = target;
        if (files) {
            const [file] = files;
            const buffer = await readAsArrayBuffer(file);
            download.uploadDbDevices(buffer);
        }
    };
    const handleKey = (key: string) => {
        switch (key) {
            case 'Escape':
                onClose();
                break;
            case 'Enter':
                handleAddClick();
                break;
        }
    };
    const handleKeyDown: KeyboardEventHandler<HTMLElement> = (e) => {
        const { key } = e;
        if (key === 'Enter') {
            e.stopPropagation();
            e.preventDefault();
            handleKey(key);
        }
    };
    const handleBtnKeyDown: KeyboardEventHandler<HTMLButtonElement> = (e) => {
        const { key } = e;
        if (key === 'Enter') {
            e.stopPropagation();
            e.preventDefault();
            const target = e.target as HTMLButtonElement;
            target.click();
        }
    };
    const handlers = {
        handleImeiChange,
        handleTypeChange,
        handlePasswordChange,
        handleSwVerChange,
        handleHwVerChange,
        handleBootVerChange,
        handleAddClick,
        handleInputClick,
        handleKeyDown,
        handleTemplateDownload,
        handleDevicesUpload,
        handleBtnKeyDown,
    };

    useEffect(() => {
        if (devices.operationSuccess) {
            onClose();
            devices.resetOperationSuccess();
        }
    }, [devices.operationSuccess]);
    useEffect(() => {
        handleInputClick();
        const element = document.activeElement as HTMLElement;
        if (element) {
            element.blur();
        }
        return () => {
            handleInputClick();
        };
    }, []);
    useEffect(() => {
        handleKey(key);
    }, [key]);

    return {
        download,
        devices,
        lang,
        state,
        handlers,
    };
};

export default useAddDeviceInDb;
